// pages/W_HR_R390.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export, 
} from "devextreme-react/data-grid";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R390({ user }) {
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const me = (custList || []).find(i =>
        i.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase()
      );
      if (me) {
        setSelectedCust(me.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    (async () => {
      setDeviceLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
        setSelectedDevice(null);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || '%',
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R390`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      
      const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        id: `${row.CUST_CD}-${row.DEVI_CD || ''}-${idx}`,
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("WIP_Status_by_Device");
    exportDataGrid({
        component: e.component,
        worksheet: worksheet,
        autoFilterEnabled: true
    }).then(() => {
        workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "WIP_Status_by_Device.xlsx");
        });
    });
    e.cancel = true;
  };

  return (
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>STATUS BY DEVICE</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: 'center' }}>
          <SelectBox
            dataSource={custList}
            value={selectedCust}
            valueExpr="CODE_CD"
            displayExpr="CODE_CD"
            onValueChanged={e => setSelectedCust(e.value)}
            disabled={comboLoading || user?.role === "CUST"}
            searchEnabled
            showClearButton={user?.role !== "CUST"}
            placeholder="Select CUST"
            label="CUST"
          />
          <SelectBox
            dataSource={deviceList}
            value={selectedDevice}
            valueExpr="CODE_CD"
            displayExpr="CODE_CD"
            onValueChanged={e => setSelectedDevice(e.value)}
            disabled={deviceLoading || !selectedCust}
            searchEnabled
            showClearButton
            placeholder="DEVICE (ALL)"
            label="DEVICE"
          />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] 중복 div 제거 및 position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
            dataSource={data}
            keyExpr="id"
            onExporting={onExporting}
            height="100%"
            showBorders={true}
            columnAutoWidth={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
        >
            <Export enabled={true} allowExportSelectedData={false} />
            <Paging defaultPageSize={10} />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />   
            <SearchPanel visible={true} width={240} highlightCaseSensitive={false} />
            <Sorting mode="multiple" />

            <Column dataField="CUST_CD" caption="CUST" fixed={true}/>
            <Column dataField="DEVI_CD" caption="DEVICE" fixed={true}/>
            <Column dataField="IN_QTY" caption="IN QTY" dataType="number" alignment="right" />
            <Column dataField="TB" caption="TB" dataType="number" alignment="right" />
            <Column dataField="IQC" caption="IQC" dataType="number" alignment="right" />
            <Column dataField="PTST" caption="PTST" dataType="number" alignment="right" />
            <Column dataField="PBKE" caption="PBKE" dataType="number" alignment="right" />
            <Column dataField="PTST2" caption="PTST2" dataType="number" alignment="right" />
            <Column dataField="INK" caption="INK" dataType="number" alignment="right" />
            <Column dataField="PTST3" caption="PTST3" dataType="number" alignment="right" />
            <Column dataField="QEI" caption="QEI" dataType="number" alignment="right" />
            <Column dataField="FQC" caption="FQC" dataType="number" alignment="right" />
            <Column dataField="WPK" caption="WPK" dataType="number" alignment="right" />
            <Column dataField="QPI" caption="QPI" dataType="number" alignment="right" />
            <Column dataField="FGS" caption="FGS" dataType="number" alignment="right" />
            <Column dataField="SHIP" caption="SHIP" dataType="number" alignment="right" />
            <Column dataField="LOSS" caption="LOSS" dataType="number" alignment="right" />
            <Column dataField="TOTAL" caption="TOTAL" dataType="number" alignment="right" />
            <Column dataField="OUTQTY" caption="OUT QTY" dataType="number" alignment="right" />
            {/* [수정] fixed={true} 추가 */}
            <Column dataField="NOWQTY" caption="NOW QTY" dataType="number" alignment="right" fixed={true} fixedPosition="right" />
        </DataGrid>
      </div>
    </div>
  );
}